import { Component, ChangeDetectorRef, Input, SimpleChanges, EventEmitter, Output, OnInit, OnChanges, OnDestroy } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { PublicationService } from '../../../services/publication.service';
import { CustomFieldsService } from '../../../services/custom-fields.service';
import { NgForm, UntypedFormGroup, UntypedFormControl, Validators } from '@angular/forms';
import { SapiService, SapiQueryResponse } from '../../../services/sapi.service';
import { BbTableService } from '../../../services/bb-table.service';
import { Location } from '@angular/common';
import { TranslateService } from '@ngx-translate/core';
import { AdBlockDetectorService } from '../../../services/ad-block-detector.service';
import { Adunit } from '../adunit/adunit-list.component';
import { BbSelectOption } from '../../shared/bb-select/bb-select.component';
import { LineItem } from '../lineitem/lineitem-details.component';
import { QueryParameter } from '../../../interfaces/queryString';
import { Subject, Subscription } from 'rxjs';
import { debounceTime } from 'rxjs/operators';

@Component({
	selector: 'adunit-details-general',
	templateUrl: './adunit-details-general.component.html',
	styleUrls: ['./adunit-details-general.component.scss']
})

export class AdunitDetailsGeneralComponent implements OnInit, OnChanges, OnDestroy {
	@Input() form: NgForm;
	@Input() formGroup: UntypedFormGroup;
	@Input() adunit: Adunit;
	@Input() lineitem: LineItem;
	@Input() isNew: boolean;

	@Output() lineitemUpdated: EventEmitter<void>;

	cmpTimeoutFormControl = new UntypedFormControl('', [Validators.min(0)]);

	outstream: boolean;
	instream: boolean;
	position: boolean;

	usedColors: string[];

	adunitShowAdvancedOptions: boolean;

	ctaMidplayOptions: BbSelectOption[];
	ctaMidplayPositionOptions: BbSelectOption[];
	ctaMidplayExitScreenOptions: BbSelectOption[];

	titleUpdatedSubject: Subject<string>;
	codeUpdatedSubject: Subject<string>;

	subscriptions: Subscription;

	allowSkipping: boolean;
	trueValue: boolean;
	keyValue: string;
	playoutKeyValue: string;

	constructor(protected route: ActivatedRoute,
		protected location: Location,
		protected publicationService: PublicationService,
		protected sapiService: SapiService,
		protected customFieldsService: CustomFieldsService,
		protected bbTableService: BbTableService,
		protected changeDetectorRef: ChangeDetectorRef,
		protected adBlockDetectorService: AdBlockDetectorService,
		protected translate: TranslateService) {

		this.adunitShowAdvancedOptions = true;
		this.lineitemUpdated = new EventEmitter<void>();

		this.trueValue = true;

		this.titleUpdatedSubject = new Subject<string>();
		this.codeUpdatedSubject = new Subject<string>();

		this.subscriptions = new Subscription();

		this.subscriptions.add(
			this.titleUpdatedSubject.pipe(
				debounceTime(400)
			).subscribe((title: string) => {
				this.titleUpdated(title);
			})
		);

		this.subscriptions.add(
			this.codeUpdatedSubject.pipe(
				debounceTime(400)
			).subscribe((code: string) => {
				this.codeUpdated(code);
			})
		);

	}

	ngOnInit() {
		this.formGroup.addControl('cmpTimeout', this.cmpTimeoutFormControl);

		this.ctaMidplayOptions = [
			{ label: this.translate.instant('HIDE'), value: 'hide', default: true },
			{ label: this.translate.instant('SHOW_IF_NO_VPAID_AD'), value: 'show_nonVPAID' },
			{ label: this.translate.instant('SHOW'), value: 'show' }
		];

		this.ctaMidplayPositionOptions = [
			{ label: this.translate.instant('TOP_RIGHT'), value: 'top_right', default: true },
			{ label: this.translate.instant('TOP_LEFT'), value: 'top_left' },
		];

		this.ctaMidplayExitScreenOptions = [
			{ label: this.translate.instant('HIDE'), value: 'hide', default: true },
			{ label: this.translate.instant('SHOW_IF_NO_COMPANION_AD'), value: 'show_nonCompanion' },
			{ label: this.translate.instant('SHOW'), value: 'show' }
		];


		const usedColors: any[] = [];

		this.sapiService
			.getUrl('/playout?facet=on&limit=0&facetfield[]=skin_backgroundColor&facetfield[]=skin_foregroundColor&facetfield[]=skin_widgetColor')
			.subscribe((response) => {
				if (response && response.facets) {
					Object.keys(response.facets).forEach((facetKey: any) => {
						const facetValues = response.facets[facetKey];
						for (let i = 0; i < facetValues.length; i += 2) {
							const color = '#' + facetValues[i].toUpperCase();

							if (color !== '#' && usedColors.indexOf(color) === -1) {
								usedColors.push(color);
							}
						}
					});
				}
				this.usedColors = usedColors;
			});
	}

	ngOnChanges(simpleChanges: SimpleChanges) {
		if (simpleChanges && simpleChanges.adunit && simpleChanges.adunit.currentValue) {
			if (this.adunit) {
				if (this.adunit.positionType === 'overlay') {
					this.position = true;
					this.outstream = false;
					this.instream = false;
				} else if (this.adunit.positionType === 'inarticle') {
					this.outstream = true;
					this.position = false;
					this.instream = false;
				} else {
					// 'preroll' or 'midroll' or 'postroll' or 'vmap'
					this.instream = true;
					this.outstream = false;
					this.position = false;
				}

				if (this.adunit.playout) {
					const playout = this.adunit.playout;
					this.setDefaultPlayoutValue('iframeBreakout', 'true');
					this.setDefaultPlayoutValue('aspectRatio', '');
					this.setDefaultPlayoutValue('commercialProgressBar', 'true');
					this.setDefaultPlayoutValue('iframeBreakout', 'true');
					this.setDefaultPlayoutValue('skipOffset', -1);
					this.allowSkipping = (this.adunit.playout.skipOffset >= 0);

					if (this.outstream) {
						if (!this.adunit.playout.responsiveSizing) {
							this.adunit.playout.responsiveSizing = 'true';
							this.adunit.playout.width = '100%';
						}
						this.setDefaultPlayoutValue('placeholderText', 'Learn more');
						this.setDefaultPlayoutValue('autoMute', 'true');
					}

					if (this.adunit.positionType === 'inarticle' && !playout.inviewMargin) {
						playout.inviewMargin = '50%';
					}

					this.setDefaultPlayoutValue('startCollapsed', 'true');
					this.setDefaultPlayoutValue('hidePlayerOnEnd', 'true');
				}
			}
		}
	}

	ngOnDestroy() {
		if (this.subscriptions) {
			this.subscriptions.unsubscribe();
		}
	}

	setDefaultPlayoutValue(key: string, defaultValue: string | number) {
		if (!this.adunit.playout[key]) {
			this.adunit.playout[key] = defaultValue;
		}
	}

	getPositionTitle() {
		if (!this.adunit || !this.adunit.positionType) {
			return this.translate.instant('AD_UNIT');
		}

		switch (this.adunit.positionType) {
			case 'preroll':
				return this.translate.instant('PREROLL');
			case 'postroll':
				return this.translate.instant('POSTROLL');
			case 'overlay':
				return this.translate.instant('OVERLAY');
			case 'inarticle':
				return this.translate.instant('INARTICLE');
			case 'vmap':
				return this.translate.instant('VMAP');
			default:
				return this.translate.instant('AD_UNIT');
		}
	}

	updateSkipOffset() {
		if (this.adunit && this.adunit.playout) {
			this.adunit.playout.skipOffset = this.allowSkipping ? 5 : -1;
			this.form.form.markAsDirty();
		}
	}

	titleUpdated(title: string) {
		this.adunit.title = title;
		this.codeUpdated(title);
	}

	codeUpdated(code: string) {
		if (code && !this.adunit.id) {
			this.adunit.code = code ? code.replace(/-/g, '*').replace(/[^\w\-]{1,}/g, '_').toLowerCase() : '';

			const queryParams: QueryParameter[] = [{ name: 'q', values: ['code:' + this.adunit.code] }];

			this.sapiService.query('adunit', queryParams).subscribe((sapiQueryResponse: SapiQueryResponse) => {
				if (sapiQueryResponse && sapiQueryResponse.totalAmount > 0) {
					this.form.form.controls.code.setErrors({ 'not-unique': true });
					this.form.form.controls.code.markAsTouched();
					this.form.form.controls.code.markAsDirty();
				}
			});
		}
	}

}
